import type { CSSProperties } from 'react'

/**
 * Overlay host contract shared by every DOM-over-camera host (the WebGPU
 * `/emails` engine and the CSS-camera product demo). Import-light on
 * purpose: `use-canvas-engine.ts` pulls the WASM package, so a host that
 * only needs the contract reads it from here.
 */

/**
 * Overlay host class. `origin-top-left` is the transform-origin contract
 * (`0 0`) — keep it in CSS, never `style={{ transformOrigin }}`. React
 * style writes on this node share the attribute the camera writer mutates
 * each rAF (`transform` + `--engine-zoom`).
 */
export const ENGINE_OVERLAY_HOST_CLASS =
  'pointer-events-none absolute top-0 left-0 h-0 w-0 origin-top-left overflow-visible will-change-transform'

/**
 * The board behind the cards: the engine's clear colour
 * (`packages/canvas-engine/src/engine.rs`, `BOARD_BG_LIGHT` 0.98 ≈ #fafafa;
 * dark matches `--background`). The light `--background` token is #fff,
 * so the light board is spelled out rather than tokenised.
 */
export const CANVAS_BOARD_BG_CLASS = 'bg-neutral-50 dark:bg-background'

/**
 * Inheritance boundary for one email card. The overlay root rewrites
 * `--engine-zoom` on EVERY zoom frame; an inherited custom-property change
 * invalidates computed style for every descendant, and each card's TipTap
 * subtree is ~10^3 elements — measured 23.5ms of forced style recalc per
 * zoom frame on a full board, vs 0.1ms with this shield. Re-declaring the
 * var as a constant on the card stops the invalidation from walking the
 * card's subtree; nothing below a card reads the live zoom (pills,
 * toolbars and headers live OUTSIDE cards and keep inheriting it).
 */
export const ENGINE_ZOOM_SHIELD_STYLE: CSSProperties = {
  ['--engine-zoom' as string]: '1',
}

/**
 * Semantic email content uses document-local coordinates. Shield the render
 * origin as well as zoom here: origin rebases must invalidate the outer frame
 * positions without walking every element in their documents. Titles and
 * handles stay outside this boundary and keep the live camera properties.
 */
export const ENGINE_DOCUMENT_CAMERA_SHIELD_STYLE = {
  ...ENGINE_ZOOM_SHIELD_STYLE,
  '--canvas-origin-x': '0px',
  '--canvas-origin-y': '0px',
} satisfies CSSProperties &
  Record<'--canvas-origin-x' | '--canvas-origin-y', string>
